'use client';

// We can not useState or useRef in a server component, which is why we are
// extracting this part out into it's own file with 'use client' on top

import {
  PersistQueryClientProvider,
  removeOldestQuery,
  type Persister
} from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import getQueryClient from '@/lib/query-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useAuth } from '@clerk/nextjs';
import { useEffect, useState, lazy, Suspense } from 'react';

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({
    default: d.ReactQueryDevtools
  }))
);

// localStorage cache so a hard reload (and chat-switch / tab-switch, which the
// in-memory cache already covers) renders prior conversation messages INSTANTLY
// from disk, then revalidates in the background — no skeleton on revisit.
const PERSIST_KEY = 'STRIQUE_RQ_CACHE_V1';
const ONE_DAY_MS = 1000 * 60 * 60 * 24;

// Only persist chat data. Persisting every query would bloat localStorage (5MB
// cap) with volatile/large payloads; we keep just what makes the chat feel
// instant. Keyed on the queryKey[0] namespace.
const PERSISTED_QUERY_NAMESPACES = new Set(['conversation', 'conversation-messages']);

/**
 * Server-side stand-in for the real persister.
 *
 * ``PersistQueryClientProvider`` MUST wrap both passes. It seeds
 * ``isRestoring`` to ``true``, while a plain ``QueryClientProvider`` leaves the
 * context at its ``false`` default — and react-query switches every
 * ``useQuery`` between optimistic and restoring results on that flag. Rendering
 * the plain provider during SSR therefore reports ``isFetching: true`` on the
 * server and ``false`` on the client's first render, so any component gated on
 * fetch state emits different markup in each pass and React tears the tree down
 * with a hydration mismatch (minified error #418).
 *
 * Nothing calls these methods on the server: restoring and subscribing both
 * happen in effects, which never run during SSR.
 */
const noopPersister: Persister = {
  persistClient: async () => {},
  restoreClient: async () => undefined,
  removeClient: async () => {}
};

// The real, localStorage-backed persister exists on the client only —
// localStorage is unavailable during SSR.
const persister: Persister =
  typeof window !== 'undefined'
    ? createSyncStoragePersister({
        storage: window.localStorage,
        key: PERSIST_KEY,
        // localStorage has a ~5MB cap; large message lists can overflow it.
        // Evict the oldest persisted query and retry instead of dropping the
        // whole cache when a write exceeds quota.
        retry: removeOldestQuery
      })
    : noopPersister;

export default function Providers({ children }: { children: React.ReactNode }) {
  const [showDevtools, setShowDevtools] = useState(false);
  const { isSignedIn } = useAuth();

  useEffect(() => {
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  // Privacy: whenever the app is in a signed-out state — explicit logout, or a
  // prior user's session expired on a shared machine — drop the persisted chat
  // cache from localStorage so one user's conversations are never restored for
  // the next. Authenticated sessions keep their cache so the instant
  // hard-reload load still works. (We clear on signed-out rather than scoping
  // the buster by userId, because Clerk resolves the id asynchronously and a
  // first-paint "anon" buster would wrongly discard a valid user's cache.)
  useEffect(() => {
    if (isSignedIn === false && typeof window !== 'undefined') {
      window.localStorage.removeItem(PERSIST_KEY);
      queryClient.clear();
    }
  }, [isSignedIn, queryClient]);

  const devtools = (
    <>
      <ReactQueryDevtools initialIsOpen />
      {showDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </Suspense>
      )}
    </>
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: ONE_DAY_MS,
        // Bust the on-disk cache if the persisted shape changes across deploys.
        buster: PERSIST_KEY,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            query.state.status === 'success' &&
            PERSISTED_QUERY_NAMESPACES.has(query.queryKey?.[0] as string)
        }
      }}>
      {children}
      {devtools}
    </PersistQueryClientProvider>
  );
}
